import { useState } from "react";
import AuthImage from "../AuthImage";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ArrowsOutSimple, LinkBreak } from "@phosphor-icons/react";
import SwipeCompare from "./SwipeCompare";

export function MarkerList({ markers = [], className = "" }) {
  if (!markers.length) return null;
  return (
    <ol className={`space-y-0.5 ${className}`} data-testid="pair-markers">
      {markers.map((m, i) => (
        <li key={i} className="flex items-start gap-1.5 text-[11px] leading-snug">
          <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white ${m.kind === "hazard" ? "bg-amber-600" : "bg-red-600"}`}>{i + 1}</span>
          <span className="text-slate-700 dark:text-slate-300">{m.label}{m.kind === "hazard" && <span className="ml-1 font-semibold text-amber-700 dark:text-amber-400">HAZARD</span>}</span>
        </li>
      ))}
    </ol>
  );
}

function Side({ label, tone, url, name, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} className="group relative block w-full overflow-hidden rounded-md border border-border bg-muted/40 text-left" data-testid={testid}>
      <span className={`absolute left-0 top-0 z-10 rounded-br-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white ${tone}`}>{label}</span>
      <div className="aspect-[4/3]"><AuthImage path={url} alt={name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" loading="lazy" /></div>
    </button>
  );
}

export function PairCard({ pair, onOpen, onUnpair, testid }) {
  const b = pair.before, a = pair.after;
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid || `pair-card-${pair.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <Side label={b.ai_markup ? "Before · marked-up" : "Before"} tone="bg-red-600" url={b.url} name={b.name} onClick={onOpen} testid={`${testid || `pair-card-${pair.id}`}-before`} />
        <Side label="After" tone="bg-emerald-600" url={a.url} name={a.name} onClick={onOpen} testid={`${testid || `pair-card-${pair.id}`}-after`} />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <MarkerList markers={b.markers} />
        <p className="text-[11px] text-muted-foreground truncate" title={a.name}>{a.uploaded_by_name ? `Taken by ${a.uploaded_by_name}` : a.name}{a.uploaded_at ? ` · ${new Date(a.uploaded_at).toLocaleDateString("en-GB")}` : ""}</p>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={onOpen} data-testid={`${testid || `pair-card-${pair.id}`}-open`}><ArrowsOutSimple size={13} /> Compare</Button>
        {onUnpair && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground" onClick={onUnpair} data-testid={`${testid || `pair-card-${pair.id}`}-unpair`}><LinkBreak size={13} /> Unpair</Button>}
      </div>
    </div>
  );
}

export function PairViewer({ pair, onClose }) {
  const [mode, setMode] = useState("swipe");
  if (!pair) return null;
  const b = pair.before, a = pair.after;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="pair-viewer">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base">Before &amp; after
            <span className="ml-auto inline-flex rounded-md border border-border p-0.5 text-xs">
              {[["swipe", "Swipe"], ["side", "Side by side"], ["before", "Before"], ["after", "After"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMode(k)} className={`rounded px-2 py-1 ${mode === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`pair-viewer-mode-${k}`}>{l}</button>
              ))}
            </span>
          </DialogTitle>
        </DialogHeader>
        {mode === "swipe" ? (
          <div className="space-y-2">
            <SwipeCompare before={b} after={a} beforeLabel={b.ai_markup ? "Before · marked-up" : "Before"} afterLabel="After" />
            <p className="text-center text-[11px] text-muted-foreground" data-testid="swipe-hint">Drag the handle to wipe between before and after</p>
            <MarkerList markers={b.markers} />
          </div>
        ) : (
        <div className={`grid gap-3 ${mode === "side" ? "md:grid-cols-2" : "grid-cols-1"}`}>
          {mode !== "after" && (
            <figure className="min-w-0">
              <div className="overflow-hidden rounded-md border border-red-300 bg-black/5"><AuthImage path={b.url} alt={b.name} className="mx-auto max-h-[62vh] w-auto object-contain" /></div>
              <figcaption className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-400">Before{b.ai_markup ? " · AI marked-up" : ""}{b.source_name ? <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground">from {b.source_name}</span> : null}</figcaption>
              <MarkerList markers={b.markers} className="mt-1" />
            </figure>
          )}
          {mode !== "before" && (
            <figure className="min-w-0">
              <div className="overflow-hidden rounded-md border border-emerald-300 bg-black/5"><AuthImage path={a.url} alt={a.name} className="mx-auto max-h-[62vh] w-auto object-contain" /></div>
              <figcaption className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">After <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground">{a.uploaded_by_name ? `taken by ${a.uploaded_by_name}` : a.name}{a.uploaded_at ? ` · ${new Date(a.uploaded_at).toLocaleString("en-GB")}` : ""}</span></figcaption>
            </figure>
          )}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
